import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { io, type Socket } from 'socket.io-client'
import type {
  BriefEvent,
  ClickTickEvent,
  ClientToServerEvents,
  CollectEvent,
  CountdownEvent,
  DisplayEvent,
  MatchConfig,
  MatchEndEvent,
  OvertimeEvent,
  Room,
  RoomErrorEvent,
  Stake,
  RoundResultEvent,
  ServerToClientEvents,
} from '@clicklinx/protocol'
import { API_URL, getAccessToken } from './api'
import { useAuth } from './auth'

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>

/**
 * Which Socket.IO transports to offer.
 *
 * WebSocket is preferred and is what local development uses. Some hosts refuse
 * the upgrade outright - AWS App Runner's ingress proxy answers 403 - and every
 * failed attempt is a console error and a wasted round trip, so the deployment
 * can pin this to `polling`.
 *
 * Long-polling costs latency but not fairness, because none of the scoring
 * depends on when a message arrives: click counts are built from offsets the
 * client stamps against round start, round timing is derived from a
 * server-supplied start timestamp against a synchronised clock, and answer
 * times are discounted by half the measured round trip.
 */
const SOCKET_TRANSPORTS = (import.meta.env.VITE_SOCKET_TRANSPORTS ?? 'websocket,polling')
  .split(',')
  .map((t) => t.trim())
  .filter(Boolean) as ('websocket' | 'polling')[]

export type MatchPhase =
  | 'idle'
  | 'countdown'
  | 'brief'
  | 'display'
  | 'collect'
  | 'result'
  | 'overtime'
  | 'ended'

interface AckFail {
  ok: false
  error: RoomErrorEvent
}
interface AckOk<T> {
  ok: true
  data: T
}
type Ack<T> = AckOk<T> | AckFail

export interface GameContextValue {
  connected: boolean
  room: Room | null
  phase: MatchPhase
  countdown: CountdownEvent | null
  brief: BriefEvent | null
  display: DisplayEvent | null
  collect: CollectEvent | null
  result: RoundResultEvent | null
  overtime: OvertimeEvent | null
  matchEnd: MatchEndEvent | null
  tick: ClickTickEvent | null
  queued: boolean
  lastError: RoomErrorEvent | null

  /** Converts a server epoch timestamp into this device's clock. */
  toLocalTime: (serverEpochMs: number) => number

  createRoom: (config: MatchConfig, isPublic: boolean) => Promise<Room>
  joinRoom: (code: string) => Promise<Room>
  leaveRoom: () => Promise<void>
  setReady: (ready: boolean) => void
  updateConfig: (config: MatchConfig) => Promise<void>
  startMatch: () => Promise<void>
  joinQueue: (config: MatchConfig) => Promise<void>
  leaveQueue: () => void
  startPractice: (mode: MatchConfig['mode'], duration: number, stake: Stake) => Promise<Room>
  sendClicks: (offsets: number[]) => void
  submitAnswer: (roundIndex: number, value: number) => void
  dismissMatch: () => void
  clearError: () => void
}

const GameContext = createContext<GameContextValue | null>(null)

export function GameProvider({ children }: { children: ReactNode }) {
  const { user, setBalance } = useAuth()
  const socketRef = useRef<GameSocket | null>(null)

  const [connected, setConnected] = useState(false)
  const [room, setRoom] = useState<Room | null>(null)
  const [phase, setPhase] = useState<MatchPhase>('idle')
  const [countdown, setCountdown] = useState<CountdownEvent | null>(null)
  const [brief, setBrief] = useState<BriefEvent | null>(null)
  const [display, setDisplay] = useState<DisplayEvent | null>(null)
  const [collect, setCollect] = useState<CollectEvent | null>(null)
  const [result, setResult] = useState<RoundResultEvent | null>(null)
  const [overtime, setOvertime] = useState<OvertimeEvent | null>(null)
  const [matchEnd, setMatchEnd] = useState<MatchEndEvent | null>(null)
  const [tick, setTick] = useState<ClickTickEvent | null>(null)
  const [queued, setQueued] = useState(false)
  const [lastError, setLastError] = useState<RoomErrorEvent | null>(null)

  /**
   * Difference between the server's clock and this device's.
   *
   * Every deadline the server sends is a server-epoch timestamp. Phones with a
   * clock a few seconds out would otherwise show a countdown that is plainly
   * wrong, or submit answers they believe are inside the window.
   */
  const clockOffsetRef = useRef(0)
  const bestRttRef = useRef(Number.POSITIVE_INFINITY)

  /** Mirrors the active match id so input handlers can read it without re-binding. */
  const currentMatchIdRef = useRef<string | null>(null)

  const toLocalTime = useCallback((serverEpochMs: number) => serverEpochMs - clockOffsetRef.current, [])

  /**
   * The connection is keyed on *who* is signed in, never on the user object.
   *
   * `wallet:update` calls `setBalance`, which produces a new user object. If
   * this effect depended on that object it would tear the socket down and
   * reconnect on every balance change - and since the server sends a balance
   * the moment you connect, that is an infinite reconnect loop. Each of those
   * disconnects also reads as the host leaving, which closes their lobby.
   */
  const userId = user?.id ?? null

  useEffect(() => {
    if (!userId) {
      socketRef.current?.disconnect()
      socketRef.current = null
      setConnected(false)
      setRoom(null)
      setPhase('idle')
      return
    }

    const socket: GameSocket = io(API_URL, {
      auth: { token: getAccessToken() },
      transports: SOCKET_TRANSPORTS,
      /**
       * Try every listed transport, not just the first.
       *
       * socket.io-client 4.8 defaults this to false, which means a list of
       * `websocket,polling` gives up entirely when the WebSocket upgrade is
       * refused rather than falling back to polling - and a proxy that refuses
       * upgrades is exactly the situation the fallback exists for. The default
       * turns a degraded connection into no connection at all.
       */
      tryAllTransports: true,
      reconnectionDelay: 400,
      reconnectionDelayMax: 4_000,
    })
    socketRef.current = socket

    const syncClock = () => {
      const sentAt = Date.now()
      socket.emit('net:ping', sentAt, (serverTime: number) => {
        const rtt = Date.now() - sentAt
        // Keep the sample from the least congested round trip; a slow one
        // carries the most error in the one-way estimate.
        if (rtt < bestRttRef.current) {
          bestRttRef.current = rtt
          // Rounded to whole milliseconds. `rtt / 2` is fractional whenever the
          // round trip is an odd number of ms, and that fraction propagates
          // into every click offset derived from this value - which the server
          // then rejects as a non-integer, silently discarding the player's
          // entire match. Sub-millisecond precision is meaningless here anyway.
          clockOffsetRef.current = Math.round(serverTime - (sentAt + rtt / 2))
        }
      })
    }

    socket.on('connect', () => {
      setConnected(true)
      bestRttRef.current = Number.POSITIVE_INFINITY
      syncClock()
    })
    socket.on('disconnect', () => setConnected(false))

    // Re-sync periodically: clocks drift, and the estimate improves with samples.
    const clockTimer = setInterval(syncClock, 12_000)

    socket.on('room:state', (next) => {
      setRoom(next)
      if (next.status === 'lobby') {
        setPhase('idle')
        setQueued(false)
      }
    })

    socket.on('room:error', (error) => {
      setLastError(error)
      // Also the end of any search. The server removes a player from the queue
      // before it discovers it cannot pair them, so leaving the spinner up
      // would show a search that no longer exists anywhere.
      setQueued(false)
    })

    socket.on('room:closed', () => {
      setRoom(null)
      setPhase('idle')
    })

    socket.on('queue:matched', (next) => {
      setQueued(false)
      setRoom(next)
    })

    socket.on('match:countdown', (event) => {
      setMatchEnd(null)
      setResult(null)
      setBrief(null)
      setDisplay(null)
      setCollect(null)
      setOvertime(null)
      setCountdown(event)
      setPhase('countdown')
    })

    socket.on('round:brief', (event) => {
      setResult(null)
      setCollect(null)
      setDisplay(null)
      setBrief(event)
      setPhase('brief')
    })

    socket.on('round:display', (event) => {
      setDisplay(event)
      setCollect(null)
      setResult(null)
      setPhase('display')
    })

    socket.on('round:collect', (event) => {
      setCollect(event)
      setPhase('collect')
    })

    socket.on('round:result', (event) => {
      setResult(event)
      setPhase('result')
    })

    socket.on('match:overtime', (event) => {
      setOvertime(event)
      setPhase('overtime')
    })

    socket.on('match:tick', (event) => setTick(event))

    socket.on('match:end', (event) => {
      setMatchEnd(event)
      setPhase('ended')
    })

    socket.on('wallet:update', (event) => setBalance(event.purse, event.balance))

    return () => {
      clearInterval(clockTimer)
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
    }
  }, [userId, setBalance])

  /** Wraps an acknowledged emit in a promise that rejects with the server's message. */
  const request = useCallback(<T,>(fn: (socket: GameSocket, ack: (r: Ack<T>) => void) => void) => {
    return new Promise<T>((resolve, reject) => {
      const socket = socketRef.current
      if (!socket) {
        reject(new Error('Not connected to the game server.'))
        return
      }
      fn(socket, (response) => {
        if (response.ok) resolve(response.data)
        else {
          setLastError(response.error)
          reject(new Error(response.error.message))
        }
      })
    })
  }, [])

  const createRoom = useCallback(
    async (config: MatchConfig, isPublic: boolean) => {
      const data = await request<{ room: Room }>((socket, ack) =>
        socket.emit('room:create', { config, isPublic }, ack),
      )
      setRoom(data.room)
      return data.room
    },
    [request],
  )

  const joinRoom = useCallback(
    async (code: string) => {
      const data = await request<{ room: Room }>((socket, ack) =>
        socket.emit('room:join', { code: code.trim().toUpperCase() }, ack),
      )
      setRoom(data.room)
      return data.room
    },
    [request],
  )

  const leaveRoom = useCallback(async () => {
    await request<null>((socket, ack) => socket.emit('room:leave', ack))
    setRoom(null)
    setPhase('idle')
    setMatchEnd(null)
  }, [request])

  const setReady = useCallback((ready: boolean) => {
    socketRef.current?.emit('room:ready', { ready }, () => {})
  }, [])

  const updateConfig = useCallback(
    async (config: MatchConfig) => {
      await request<{ room: Room }>((socket, ack) => socket.emit('room:config', { config }, ack))
    },
    [request],
  )

  const startMatch = useCallback(async () => {
    await request<null>((socket, ack) => socket.emit('room:start', ack))
  }, [request])

  const joinQueue = useCallback(
    async (config: MatchConfig) => {
      // Marked as searching before the request, not after it.
      //
      // The server emits `queue:matched` before acknowledging `queue:join`, so
      // for the player who completes a pairing the ack resolves *after* the
      // match handler has already cleared this flag. Setting it afterwards left
      // them permanently "Searching" with every action disabled, while the
      // server had no queue entry for them at all.
      setQueued(true)
      try {
        await request<null>((socket, ack) =>
          socket.emit(
            'queue:join',
            {
              mode: config.mode,
              stake: config.stake,
              duration: config.duration,
              purse: config.purse,
            },
            ack,
          ),
        )
      } catch (error) {
        setQueued(false)
        throw error
      }
    },
    [request],
  )

  const leaveQueue = useCallback(() => {
    socketRef.current?.emit('queue:leave', () => {})
    setQueued(false)
  }, [])

  const startPractice = useCallback(
    async (mode: MatchConfig['mode'], duration: number, stake: Stake) => {
      const data = await request<{ room: Room }>((socket, ack) =>
        socket.emit('practice:start', { mode, duration, stake }, ack),
      )
      setRoom(data.room)
      return data.room
    },
    [request],
  )

  useEffect(() => {
    currentMatchIdRef.current = room?.matchId ?? countdown?.matchId ?? null
  }, [room?.matchId, countdown?.matchId])

  const sendClicks = useCallback((offsets: number[]) => {
    const matchId = currentMatchIdRef.current
    if (!matchId || offsets.length === 0) return
    socketRef.current?.emit('game:clicks', { matchId, offsets })
  }, [])

  const submitAnswer = useCallback((roundIndex: number, value: number) => {
    const matchId = currentMatchIdRef.current
    if (!matchId) return
    socketRef.current?.emit('game:answer', { matchId, roundIndex, value }, () => {})
  }, [])

  const dismissMatch = useCallback(() => {
    setMatchEnd(null)
    setResult(null)
    setDisplay(null)
    setCollect(null)
    setBrief(null)
    setTick(null)
    setPhase('idle')
  }, [])

  const clearError = useCallback(() => setLastError(null), [])

  const value = useMemo<GameContextValue>(
    () => ({
      connected,
      room,
      phase,
      countdown,
      brief,
      display,
      collect,
      result,
      overtime,
      matchEnd,
      tick,
      queued,
      lastError,
      toLocalTime,
      createRoom,
      joinRoom,
      leaveRoom,
      setReady,
      updateConfig,
      startMatch,
      joinQueue,
      leaveQueue,
      startPractice,
      sendClicks,
      submitAnswer,
      dismissMatch,
      clearError,
    }),
    [
      connected,
      room,
      phase,
      countdown,
      brief,
      display,
      collect,
      result,
      overtime,
      matchEnd,
      tick,
      queued,
      lastError,
      toLocalTime,
      createRoom,
      joinRoom,
      leaveRoom,
      setReady,
      updateConfig,
      startMatch,
      joinQueue,
      leaveQueue,
      startPractice,
      sendClicks,
      submitAnswer,
      dismissMatch,
      clearError,
    ],
  )

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>
}

export function useGame(): GameContextValue {
  const context = useContext(GameContext)
  if (!context) throw new Error('useGame must be used inside <GameProvider>')
  return context
}
